import React, { useContext, useEffect, VFC } from 'react';

import { AxiosInstance } from 'axios';
import { get, keys, noop, pickBy } from 'lodash';

import { cwaUrl } from '/configuration';
import {
  ArticleMetadata,
  FavoritesPanelProps,
  PublishStatus,
  Mntl,
  CustomPanelType,
  HandlePresentType,
  TrackPresentPanelDownloadFn,
  UserAccessRestrictions,
} from '/types';
import { Asset, assetUri } from '/utils/assetUri';
import {
  AppShellContext,
  FavoritesContext,
  MetadataContext,
  PublishStatusContext,
} from '/utils/contexts';
import { useCwaAxios } from '/utils/cwaAxios';
import useTranslate, { Trans } from '/utils/translations';
import useTracking, {
  getContentTrackingPropertiesFromMetadata,
} from '/utils/useTracking';
import useUserAccessRestrictions from '/utils/useUserAccessRestrictions';

import AccessPanel from './AccessPanel';
import fileDownload from './fileDownload';
import LongTermPreviewPanel from './LongTermPreviewPanel';
import PresentPanel from './PresentPanel';

const PRODUCT_ID_REPORT =
  'workspace_SpacesStore_fa98f549-7fe9-4af0-8ade-b7476efdbc79';

// These flags provide reports access independent of accessible_codes
const REPORTS_INDEPENDENT_FLAGS = [
  'hasWebinarItems',
  'hasQuarterlyUpdate',
  'hasEditorials',
  'hasTIM',
];

type PanelsType =
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  | Record<string, Record<string, any>>
  | {
      CustomPanel?: CustomPanelType[];
    };

const calculateDownloadPath = (
  publishStatus: PublishStatus,
  contentType: string,
  metadata: ArticleMetadata,
): string | undefined => {
  if (publishStatus === 'live') {
    return `${cwaUrl}/download/${contentType}/${metadata.slug}`;
  }
  if (publishStatus === 'preview') {
    return `${cwaUrl}/download/preview/article/${metadata.id}`;
  }
  return undefined;
};

export const createDownloadButtons = (
  downloadPath: string | undefined,
  infographicId: string | undefined,
  hasExecutiveSummary: boolean,
  metadata: ArticleMetadata,
  publishStatus: PublishStatus,
  t: CallableFunction,
  cwaAxiosTaskbar: AxiosInstance,
  isAcademicUser: boolean,
): Record<string, unknown>[] => {
  const downloads = [];
  const metadataTrackingProperties = getContentTrackingPropertiesFromMetadata(
    metadata,
  );
  const isReport = metadata.product.id.includes(PRODUCT_ID_REPORT);

  if (!!infographicId) {
    downloads.push({
      title: t('Infographic overview'),
      description: t(
        'Data visualizations of key insights, curated by our experts.',
      ),
      downloads: [
        {
          url: assetUri(infographicId, Asset.Attachment, publishStatus),
          label: t('Download PDF'),
          mixpanelLocation: 'Sidebar',
          mixpanelProperties: {
            ...metadataTrackingProperties,
            Action: 'Download Infographic Overview',
            Panel: 'Download',
            Format: '.pdf',
          },
        },
      ],
    });
  }

  if (hasExecutiveSummary) {
    downloads.push({
      title: t('Executive summary'),
      description: t('Key findings and expert recommendations.'),
      downloads: [
        {
          url: `${downloadPath}/pdf-async?executive_summary=true`,
          label: t('Download PDF'),
          mixpanelLocation: 'Sidebar',
          mixpanelProperties: {
            ...metadataTrackingProperties,
            Action: 'Download Executive Summary',
            Panel: 'Download',
            Format: '.pdf',
          },
          fileDownload: (url: string): Promise<void> =>
            fileDownload(cwaAxiosTaskbar, url),
        },
      ],
    });
  }

  if (!!downloadPath && !(isReport && isAcademicUser)) {
    const fullReportDownloads = [
      {
        url: `${downloadPath}/pptx-async`,
        label: t('Download PPT'),
        mixpanelLocation: 'Sidebar',
        mixpanelProperties: {
          ...metadataTrackingProperties,
          Action: 'Download PPT',
          Panel: 'Download',
          Format: '.ppt',
        },
        fileDownload: (url: string): Promise<void> =>
          fileDownload(cwaAxiosTaskbar, url),
      },
      {
        url: `${downloadPath}/pdf-async`,
        label: t('Download PDF'),
        mixpanelLocation: 'Sidebar',
        mixpanelProperties: {
          ...metadataTrackingProperties,
          Action: 'Download PDF',
          Panel: 'Download',
          Format: '.pdf',
        },
        fileDownload: (url: string): Promise<void> =>
          fileDownload(cwaAxiosTaskbar, url),
      },
    ];
    downloads.push({
      title: t('Full report'),
      description: t('The in-depth analysis behind the insights.'),
      downloads: fullReportDownloads,
    });
  }

  return downloads;
};

export const getPanels = (
  t: CallableFunction,
  cwaAxiosArticle: AxiosInstance,
  cwaAxiosTaskbar: AxiosInstance,
  contentType: string,
  publishStatus: PublishStatus,
  metadata: ArticleMetadata,
  username: string | undefined,
  mntlJsonString: string,
  accessRestrictionsString: string,
  handlePresent: HandlePresentType,
  favoritesPanelProps: FavoritesPanelProps,
  trackAccessPanel: () => void,
  trackLongTermPreview: (action: string) => void,
  trackPresentPanelDownload: TrackPresentPanelDownloadFn,
): PanelsType => {
  const mntl = JSON.parse(mntlJsonString) as Mntl;
  const accessRestrictions = JSON.parse(
    accessRestrictionsString,
  ) as UserAccessRestrictions;
  const { clients, oxygen, gnpd } = mntl.access;
  const downloadPath = gnpd?.disable_downloads
    ? undefined
    : calculateDownloadPath(publishStatus, contentType, metadata);
  const infographicId = metadata?.resources
    ?.filter((attachment) => attachment.type === 'infographic-overview')
    .pop()?.id;
  const hasExecutiveSummary = !!metadata.has_executive_summary;
  const isCustom = 'custom_access' in metadata;
  const isLongTermPreview = publishStatus === 'long_term_preview';
  const isMintel = mntl.org === 'Mintel';
  const isOxygenUser =
    !!get(oxygen, 'accessible_codes', []).length ||
    REPORTS_INDEPENDENT_FLAGS.some((flag: string): boolean =>
      oxygen ? oxygen[flag] : false,
    );
  const isAcademicUser = accessRestrictions.isAcademicUser;

  const builtInPanels = {
    DownloadPanel: {
      t: t,
      downloads: createDownloadButtons(
        downloadPath,
        infographicId,
        hasExecutiveSummary,
        metadata,
        publishStatus,
        t,
        cwaAxiosTaskbar,
        isAcademicUser,
      ),
    },
    ProjectPanel: {
      username: username,
      isOxygenUser: isOxygenUser,
      pathBase: `${cwaUrl}/`,
      auth: 'portalJwt',
      usePolling: true,
    },
    SharePanel: {
      path: `${cwaUrl}/api/share-article`,
      auth: 'portalJwt',
      content: { articleId: metadata.id },
      mixpanelLocation: 'Sidebar',
      mixpanelProperties: {
        ...getContentTrackingPropertiesFromMetadata(metadata),
        Panel: 'Share',
      },
    },
    HelpPanel: {
      path: `${cwaUrl}/api/help-message`,
      auth: 'portalJwt',
    },
    FeedbackPanel: {
      t: t,
      path: `${cwaUrl}/api/article-feedback`,
      auth: 'portalJwt',
      content: { articleId: metadata.id },
    },
    FavoritesPanel: favoritesPanelProps,
  };

  const downloadsAvailable = builtInPanels.DownloadPanel.downloads.length > 0;

  const canEdit =
    metadata['editor']['email'] === mntl.mail ||
    mntl.flags.includes('cwa-edit-all-articles');

  const panelFlags: Record<string, boolean> = {
    DownloadPanel:
      !accessRestrictions.isLibraryLockdownUser &&
      !isLongTermPreview &&
      !!downloadsAvailable,
    ProjectPanel:
      !isLongTermPreview &&
      !accessRestrictions.toolsDisabled &&
      !accessRestrictions.isLibraryLockdownUser,
    AccessPanel:
      !isLongTermPreview &&
      isMintel &&
      !accessRestrictions.isMasquerading &&
      isCustom,
    SharePanel: !isLongTermPreview && !accessRestrictions.toolsDisabled,
    HelpPanel: !isLongTermPreview && !!clients?.contacting,
    FeedbackPanel: !isLongTermPreview,
    EditPanel: !isLongTermPreview && canEdit,
    PresentPanel: isMintel && !accessRestrictions.toolsDisabled,
    LongTermPreviewPanel:
      canEdit && (publishStatus === 'preview' || isLongTermPreview),
    FavoritesPanel:
      !accessRestrictions.isLibraryLockdownUser &&
      !accessRestrictions.isAnonymousUser &&
      !isLongTermPreview,
  };

  const CustomPanels: Record<string, CustomPanelType> = {
    AccessPanel: AccessPanel(
      cwaAxiosTaskbar,
      `/api/article-access/${publishStatus}/${metadata.id}/`,
      trackAccessPanel,
    ),
    EditPanel: {
      closeSidebar: noop,
      iconName: 'edit',
      url: `${metadata['protocol']}://${metadata['hostname']}${metadata['mintel_editor_link']}`,
      title: <Trans>Edit</Trans>,
      color: 'secondary',
      width: 0,
      id: 'edit-panel',
    },
    PresentPanel: PresentPanel(
      handlePresent,
      cwaAxiosTaskbar,
      downloadPath,
      trackPresentPanelDownload,
    ),
    LongTermPreviewPanel: LongTermPreviewPanel(
      cwaAxiosArticle,
      isLongTermPreview,
      metadata.id,
      trackLongTermPreview,
    ),
  };

  return {
    ...pickBy(builtInPanels, (_, panel) => panelFlags[panel]),
    CustomPanel: keys(CustomPanels)
      .filter((panel) => panelFlags[panel])
      .map((panel) => CustomPanels[panel]),
  };
};

const sidebarProps = {
  product: 'effortless',
};

type TaskbarProps = {
  cwaAxiosArticle: AxiosInstance;
  contentType: string;
  handlePresent: HandlePresentType;
};

const Taskbar: VFC<TaskbarProps> = ({
  cwaAxiosArticle,
  contentType,
  handlePresent,
}) => {
  const metadata = useContext(MetadataContext);
  const { accessDetails, configureSidebar } = useContext(AppShellContext);
  const publishStatus = useContext(PublishStatusContext);
  const mntlJsonString =
    accessDetails?.mntl && JSON.stringify(accessDetails.mntl);
  const username = accessDetails?.sub;
  const accessRestrictionsString = JSON.stringify(useUserAccessRestrictions());
  const cwaAxiosTaskbar = useCwaAxios();
  const t = useTranslate();
  const { favoritesPanelProps } = useContext(FavoritesContext);
  const {
    trackAccessPanel,
    trackLongTermPreview,
    trackPresentPanelDownload,
  } = useTracking();

  useEffect(() => {
    if (mntlJsonString) {
      configureSidebar(
        getPanels(
          t,
          cwaAxiosArticle,
          cwaAxiosTaskbar,
          contentType,
          publishStatus,
          metadata,
          username,
          mntlJsonString,
          accessRestrictionsString,
          handlePresent,
          favoritesPanelProps,
          trackAccessPanel,
          trackLongTermPreview,
          trackPresentPanelDownload,
        ),
        sidebarProps,
      );
    }
  }, [
    t,
    cwaAxiosArticle,
    cwaAxiosTaskbar,
    configureSidebar,
    contentType,
    publishStatus,
    metadata,
    username,
    mntlJsonString,
    accessRestrictionsString,
    handlePresent,
    favoritesPanelProps,
    trackAccessPanel,
    trackLongTermPreview,
    trackPresentPanelDownload,
  ]);

  return <></>; // Intentionally Renderless
};

export default Taskbar;
